import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';

const CycleTimes = () => {
  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({ machine_id: '', machine_name: '', product_sku: '', product_name: '', ideal_cycle_time_sec: 0 });

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const [cRes, mRes, iRes] = await Promise.all([api.get('/production/cycle-times'), api.get('/production/machines'), api.get('/sales/inventory')]);
      setItems(cRes.data); setMachines(mRes.data); setInventory(iRes.data);
    } catch(e){} finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setFormData({ machine_id: '', machine_name: '', product_sku: '', product_name: '', ideal_cycle_time_sec: 0 }); setShowModal(true); };
  const openEdit = (c) => { setEditing(c); setFormData({ machine_id: c.machine_id, machine_name: c.machine_name, product_sku: c.product_sku, product_name: c.product_name, ideal_cycle_time_sec: c.ideal_cycle_time_sec }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/production/cycle-times/${editing.id}`, formData);
      else await api.post('/production/cycle-times', formData);
      setShowModal(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this cycle time?')) return;
    try { await api.delete(`/production/cycle-times/${id}`); fetchData(); } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const filtered = items.filter(c => c.machine_name?.toLowerCase().includes(searchTerm.toLowerCase()) || c.product_name?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div data-testid="cycle-times-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Cycle Time Master</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="add-cycle-btn"><Plus size={16} /> Add Cycle Time</button>
      </div>
      <div className="card mb-4">
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input type="text" className="form-input" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
        </div>
      </div>
      <div className="card">
        {loading ? <div>Loading...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>Machine</th><th>Product SKU</th><th>Product Name</th><th>Ideal Cycle Time (sec)</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No cycle times found</td></tr> : filtered.map(c => (
                  <tr key={c.id}>
                    <td>{c.machine_name}</td>
                    <td className="font-mono">{c.product_sku}</td>
                    <td>{c.product_name}</td>
                    <td className="tabular-nums">{c.ideal_cycle_time_sec}s</td>
                    <td><span className={`badge ${c.status === 'active' ? 'badge-success' : 'badge-danger'}`}>{c.status}</span></td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(c)} className="btn btn-ghost btn-sm"><Edit size={14} /></button>
                        <button onClick={() => handleDelete(c.id)} className="btn btn-ghost btn-sm"><Trash2 size={14} color="#DC2626" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showModal && (
        <div className="modal-overlay" data-testid="cycle-modal">
          <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? 'Edit Cycle Time' : 'Add Cycle Time'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Machine *</label>
                  <select className="form-select" value={formData.machine_id} onChange={(e) => { const m = machines.find(x => x.id === e.target.value); setFormData({...formData, machine_id: e.target.value, machine_name: m?.name || ''}); }} required data-testid="machine-select">
                    <option value="">Select Machine</option>
                    {machines.map(m => <option key={m.id} value={m.id}>{m.name} ({m.machine_code})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Product *</label>
                  <select className="form-select" value={formData.product_sku} onChange={(e) => { const inv = inventory.find(x => x.sku === e.target.value); setFormData({...formData, product_sku: e.target.value, product_name: inv?.name || ''}); }} required data-testid="product-select">
                    <option value="">Select Product</option>
                    {inventory.map(i => <option key={i.sku} value={i.sku}>{i.sku} - {i.name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Ideal Cycle Time (seconds) *</label><input type="number" className="form-input" value={formData.ideal_cycle_time_sec} onChange={(e) => setFormData({...formData, ideal_cycle_time_sec: parseInt(e.target.value) || 0})} required data-testid="cycle-input" /></div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-cycle-btn">{editing ? 'Update' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CycleTimes;
